/**
 * Messwerteingabe für die Kalibrierung eines 4-Gas Abgastesters
 */

import { observable, computed, action } from 'mobx';
import { observer } from 'mobx-react';
import * as React from 'react';
import { bind } from '../../../utils/decorators';
import { FormControl, Table, Panel, ControlLabel, FormGroup, Col, Checkbox, Popover, OverlayTrigger, Glyphicon } from 'react-bootstrap';
import { inject } from '../../../utils/decorators';
import { VorgangStore } from '../../../stores/VorgangStore';
import { Agt4GasKalibrierungStore } from '../../../stores/Agt4GasKalibrierungStore';
import { IArbeitsmittel } from '../../../models/ApiTypes';
import Agt4GasMessungKomponente from './Agt4GasMessungKomponente';

export interface IAgt4GasMessungProps {
  pruefgasArt: PruefgasArt;
  genauigkeitsklasse: string;
  formdataprefix: string;
  value?: string;
  nachjustage: boolean;
}

export enum PruefgasArt {
  A = 'AGT_GEMISCH_A',
  B = 'AGT_GEMISCH_B',
  C = 'AGT_GEMISCH_C',
  O2 = 'AGT_GEMISCH_O2'
}

interface IAgt4GasHintIconProps {
  hint: string;
}

class Agt4GasHintIcon extends React.Component<IAgt4GasHintIconProps, {}> {

  public render() {
    return (
      <OverlayTrigger trigger={['hover', 'focus', 'click']} placement="bottom" overlay={<Popover id="popover-trigger-hover-focus">{this.props.hint}</Popover>}>
        <div><Glyphicon glyph="question-sign" width="36px" height="36px" style={{verticalAlign: 'bottom'}}/></div>
      </OverlayTrigger>
    );
  }

}

interface IAgt4GasRadioGroup {
  name: string;
  checked: boolean;
  onHandleChange: (value: number) => void;
}

@observer
class Agt4GasRadioGroup extends React.Component<IAgt4GasRadioGroup, {}> {

  @inject(Agt4GasKalibrierungStore) kalibrierungStore: Agt4GasKalibrierungStore;

  @observable checked: boolean;

  constructor(props: IAgt4GasRadioGroup) {
    super(props);
    this.checked = this.props.checked;
  }

  public render() {
    return (
      <div>
        <div className="radio radio-success radio-inline">
          <input
            type="radio"
            name={this.props.name}
            id={`1_${this.props.name}`}
            checked={this.props.checked}
            disabled={this.kalibrierungStore.readOnly}
            onChange={e => { this.props.onHandleChange(1); }}
            value="on"/>
          <label htmlFor={`1_${this.props.name}`}>in Ordnung</label>
        </div>
        <div className="radio radio radio-inline">
          <input
            type="radio"
            name={this.props.name}
            id={`0_${this.props.name}`}
            checked={!this.props.checked}
            disabled={this.kalibrierungStore.readOnly}
            onChange={e => { this.props.onHandleChange(0); }}
            value="off"/>
          <label htmlFor={`0_${this.props.name}`}>nicht in Ordnung</label>
        </div>
      </div>
    );
  }

}


@observer
export default class Agt4GasMessung extends React.Component<IAgt4GasMessungProps, {}> {

    @inject(VorgangStore) vorgangStore: VorgangStore;
    @inject(Agt4GasKalibrierungStore) kalibrierungStore: Agt4GasKalibrierungStore;

    @observable validationstate: 'success' | 'warning' | 'error' | undefined = 'error';

    @observable propanhexanfaktor: number | undefined = undefined;
    @observable propanwertverwenden: boolean = false;
    @observable arbeitsmittel: IArbeitsmittel | undefined = undefined;
    @observable gespeichertesArbeitsmittel: string | undefined;
    @observable ansprechzeitO2: boolean = false;
    @observable ansprechzeitCO: boolean = false;

    constructor(props: IAgt4GasMessungProps) {
      super(props);
      // Vorhandene Werte übernehmen
      if (props.value) {
        let obj = JSON.parse(props.value);
        if (obj.pef) {
          this.propanhexanfaktor = obj.pef;
        }
        if (obj.propanwertverwenden) {
          this.propanwertverwenden = obj.propanwertverwenden === 'on';
        }
        if (obj.ansprechzeito2) {
          this.ansprechzeitO2 = obj.ansprechzeito2 === 'on';
        }
        if (obj.ansprechzeitco) {
          this.ansprechzeitCO = obj.ansprechzeitco === 'on';
        }
        if (obj.arbeitsmittel) {
          if (obj.arbeitsmittel) {
            this.setGespeichertesArbeitsmittel(obj.arbeitsmittel.guid);
          }
        }
      }
    }

    private pruefgasKuerzel(art: PruefgasArt) {
      switch (art) {
        case PruefgasArt.A:
          return 'A';
        case PruefgasArt.B:
          return 'B';
        case PruefgasArt.C:
          return 'C';
        case PruefgasArt.O2:
          return 'O\u2082';
        default:
          return '';
      }
    }

    @computed get titel() {
      let text: string = 'Messung mit Prüfgas ' + this.pruefgasKuerzel(this.props.pruefgasArt);
      if (this.props.nachjustage) {
        text += ' nach Justage';
      }
      return text;
    }

    @computed get formdataName() {
      return `${this.props.formdataprefix}`;
    }

    /**
     * Gibt an, ob die Eingabe des Propan/Hexan-Faktors anhand der gesetzten Prüfgasarten notwendig ist
     * @returns true wenn die Eingabe des PEFs notwendig ist
     */
    @computed get eingabePEF() {
      return this.props.pruefgasArt !== PruefgasArt.O2;
    }

    /**
     * Gibt an, ob die Angabe der Ansprechzeiten für O2 und CO bei einer der gesetzten Prüfgasarten notwendig ist
     * @returns true wenn die Angabe notwendig ist
     */
    @computed get eingabeAnsprechzeiten() {
      return this.props.pruefgasArt === PruefgasArt.A;
    }

    @action setArbeitsmittel(arbeitsmittel: IArbeitsmittel | undefined) {
      this.arbeitsmittel = arbeitsmittel;
    }

    @action setGespeichertesArbeitsmittel(arbeitsmittelguid: string | undefined) {
      this.gespeichertesArbeitsmittel = arbeitsmittelguid;
      if (arbeitsmittelguid) {
        // Gespeichertes Arbeitsmittel im aktuellen Vorgang suchen und als Interface in Auswertung übernehmen
        if (this.vorgangStore.vorgang && this.vorgangStore.vorgang.arbeitsmittel) {
          let arbeitsmittel = this.vorgangStore.vorgang.arbeitsmittel.find((am) => (am.arbeitsmittelguid === arbeitsmittelguid));
          if (arbeitsmittel) {
            this.setArbeitsmittel(arbeitsmittel);
          }
        }
      }
    }

    @bind @action handlePEFChange(ev: React.FormEvent<FormControl>) {
      this.propanhexanfaktor = (ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).valueAsNumber : undefined;
    }

    @bind @action handlePropanUmrechnungChange(ev: React.FormEvent<FormControl>) {
      this.propanwertverwenden = (ev.target as HTMLInputElement).checked;
    }

    @action handleAnsprechzeitO2Changed = (value: number) => {
      this.ansprechzeitO2 = (value === 1);
    }

    // @bind @action handleAnsprechzeitCOChanged(value: number) {
    @action handleAnsprechzeitCOChanged = (value: number) => {
      this.ansprechzeitCO = (value === 1);
    }

    @bind @action handleArbeitsmittelChanged(ev: React.FormEvent<FormControl>) {
      const arbeitsmittelguid = (ev.target as HTMLInputElement).value !== '' ? (ev.target as HTMLInputElement).value : undefined;
      if (arbeitsmittelguid && this.vorgangStore.vorgang && this.vorgangStore.vorgang.arbeitsmittel) {
        const arbeitsmittel = this.vorgangStore.vorgang.arbeitsmittel.find(am => am.arbeitsmittelguid === arbeitsmittelguid);
        this.setArbeitsmittel(arbeitsmittel);
      } else {
        this.setArbeitsmittel(undefined);
      }
    }

    public renderArbeitsmittelAuswahl() {
      let arbeitsmittel: IArbeitsmittel[] = [];
      // Zur Art des Prüfgases der Komponente passenden Arbeitsmittel aus aktuell gewähltem Set ermitteln
      if (this.vorgangStore.vorgang && this.vorgangStore.vorgang.arbeitsmittel) {
        arbeitsmittel = this.vorgangStore.vorgang.arbeitsmittel.filter(am => {
          if (this.props.pruefgasArt === am.typ) {
            return true;
          }
          return false;
        });
        return (
          <div>
            <FormGroup validationState={this.arbeitsmittel !== undefined ? undefined : 'error'} controlId="Verwendetes Prüfgas">
              <Col componentClass={ControlLabel} sm={4}>Verwendetes Prüfgas</Col>
              <Col sm={8}>
                {!this.kalibrierungStore.readOnly ?
                  <FormControl
                    componentClass="select"
                    placeholder="select"
                    onChange={this.handleArbeitsmittelChanged}
                    defaultValue={this.gespeichertesArbeitsmittel}
                    name={`${this.formdataName}[arbeitsmittel][guid]`}
                  >
                    <option value={undefined}/>
                    {arbeitsmittel.map(am => <option key={am.arbeitsmittelguid} value={am.arbeitsmittelguid}>{am.name} ({am.arbeitsmittelnr})</option>)}
                  </FormControl>
                  :
                  <FormControl
                    type="text"
                    value={this.arbeitsmittel && `${this.arbeitsmittel.name} (${this.arbeitsmittel.arbeitsmittelnr})`}
                    readOnly={true}
                    disabled={true}
                  />
                }
                <FormControl type="hidden" name={`${this.formdataName}[arbeitsmittel][nr]`} value={this.arbeitsmittel ? this.arbeitsmittel.arbeitsmittelnr : ''}/>
                <FormControl type="hidden" name={`${this.formdataName}[arbeitsmittel][name]`} value={this.arbeitsmittel ? this.arbeitsmittel.name : ''}/>
              </Col>
            </FormGroup>
            {/* {arbeitsmittel ? arbeitsmittel.map(am => am.name).join(', ') : 'keine passenden Arbeitsmittel gefunden'} */}
          </div>
        );
      }
      return (<div>Keine Arbeitsmittel im Vorgang hinterlegt!!!</div>);
    }

    public render() {
      return(
        <Panel header={this.titel} collapsible={true} defaultExpanded={true}>
          {this.renderArbeitsmittelAuswahl()}
          {this.eingabePEF && (
            <FormGroup validationState={this.propanhexanfaktor !== undefined ? undefined : 'error'} controlId="Propan/Hexanfaktor">
              <Col componentClass={ControlLabel} sm={4}>Propan/Hexanfaktor</Col>
              <Col sm={4}>
                {!this.kalibrierungStore.readOnly ?
                  <FormControl
                    type="number"
                    step="any"
                    value={this.propanhexanfaktor}
                    name={`${this.formdataName}[pef]`}
                    onChange={this.handlePEFChange}
                  />
                  :
                  <FormControl
                    type="number"
                    step="any"
                    value={this.propanhexanfaktor}
                    readOnly={true}
                    disabled={true}
                  />
                }
              </Col>
              <Col sm={4}>
                <Checkbox
                  inline={true}
                  name={`${this.formdataName}[propanwertverwenden]`}
                  disabled={this.kalibrierungStore.readOnly}
                  onChange={!this.kalibrierungStore.readOnly ? this.handlePropanUmrechnungChange : undefined}
                  defaultChecked={this.propanwertverwenden}
                >
                  Propanwert berechnen
                </Checkbox>
              </Col>
            </FormGroup>
          )}
          <Table striped={false} bordered={false} condensed={true} hover={false} className="agt-auswertung" style={{maxWidth: '80%', margin: 'auto', marginBottom: '20px'}}>
            <thead>
              <tr>
                  <th/>
                  <th>Sollwert</th>
                  <th>Messwert</th>
                  <th>Toleranzbereich</th>
              </tr>
            </thead>
            <tbody>
              {/* Info über Nutzung der Messunsicherheit als unsichtbares Feld in Request übergeben */}
              <FormControl type="hidden" name={`${this.formdataName}[messunsicherheitanwenden]`} value={this.kalibrierungStore.messunsicherheitAnwenden ? 'true' : 'false'}/>

              {this.props.pruefgasArt !== PruefgasArt.O2 &&
                <Agt4GasMessungKomponente
                  genauigkeitsklasse={this.props.genauigkeitsklasse}
                  beschriftung="CO"
                  einheit="%vol"
                  bezeichner="komponente_co"
                  propanwertverwenden={this.propanwertverwenden}
                  propanhexanfaktor={this.propanhexanfaktor}
                  formdataprefix={`${this.props.formdataprefix}[co]`}
                  value={this.props.value}
                  arbeitsmittel={this.arbeitsmittel}
                />
              }
              {this.props.pruefgasArt !== PruefgasArt.O2 &&
                <Agt4GasMessungKomponente
                  genauigkeitsklasse={this.props.genauigkeitsklasse}
                  beschriftung="CO\u2082"
                  einheit="%vol"
                  bezeichner="komponente_co2"
                  propanwertverwenden={this.propanwertverwenden}
                  propanhexanfaktor={this.propanhexanfaktor}
                  formdataprefix={`${this.props.formdataprefix}[co2]`}
                  value={this.props.value}
                  arbeitsmittel={this.arbeitsmittel}
                />
              }
              {this.props.pruefgasArt !== PruefgasArt.O2 &&
                <Agt4GasMessungKomponente
                  genauigkeitsklasse={this.props.genauigkeitsklasse}
                  beschriftung="HC"
                  einheit="ppm vol"
                  bezeichner="komponente_hc"
                  propanwertverwenden={this.propanwertverwenden}
                  propanhexanfaktor={this.propanhexanfaktor}
                  formdataprefix={`${this.props.formdataprefix}[hc]`}
                  value={this.props.value}
                  arbeitsmittel={this.arbeitsmittel}
                />
              }
              <Agt4GasMessungKomponente
                genauigkeitsklasse={this.props.genauigkeitsklasse}
                beschriftung="O\u2082"
                einheit="%vol"
                bezeichner="komponente_o2"
                // @TODO: Für O2 eigentlich nicht nötig, evtl. als optionale Props deklarieren
                propanwertverwenden={this.propanwertverwenden}
                propanhexanfaktor={this.propanhexanfaktor}
                formdataprefix={`${this.props.formdataprefix}[o2]`}
                value={this.props.value}
                arbeitsmittel={this.arbeitsmittel}
              />
            </tbody>
          </Table>
          {this.eingabeAnsprechzeiten &&
            <div>
              <FormGroup controlId="ansprechzeitO2">
                <Col componentClass={ControlLabel} sm={3}>O<sub>2</sub>-Ansprechzeit</Col>
                <Col sm={8}>
                  <Agt4GasRadioGroup
                    name={`${this.formdataName}[ansprechzeito2]`}
                    checked={this.ansprechzeitO2}
                    onHandleChange={this.handleAnsprechzeitO2Changed}
                  />
                </Col>
                <Col sm={1}>
                  <Agt4GasHintIcon hint="Beim Wechsel von 20,9 %vol auf 0 %vol O2 in 60 Sekunden einen Wert von weniger als 0,1 %vol anzeigen."/>
                </Col>
              </FormGroup>
              <FormGroup controlId="ansprechZeitCO">
                <Col componentClass={ControlLabel} sm={3}>CO-Ansprechzeit</Col>
                <Col sm={8}>
                  <Agt4GasRadioGroup
                    name={`${this.formdataName}[ansprechzeitco]`}
                    checked={this.ansprechzeitCO}
                    onHandleChange={this.handleAnsprechzeitCOChanged}
                  />
                </Col>
                <Col sm={1}>
                  <Agt4GasHintIcon hint="Beim Einleiten eines CO-haltigen Gases muss der CO-Wert auf mind. 95 % des Endwertes innerhalb von 15 Sekunden ansteigen."/>
                </Col>
              </FormGroup>
            </div>
          }
        </Panel>
      );
    }
}



// WEBPACK FOOTER //
// ./~/tslint-loader!./src/components/vorlagen/agt/Agt4GasMessung.tsx